import AntIcon from "@/components/icons/AntIcon";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import { URLQuery } from "@/utils/queryUtils";
import { Button, Card, Col, Radio, Row } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { QueryKeys, rowGutter } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { hasAllPermissions, hasPermission } from "../../../../common/security/utils";
import { usePermissions } from "../../../auth/hooks";
import { CalcType } from "../../enums";
import { CalcSettingsListView } from "../components/CalcSettingsListView";
import { ADMIN_CALC_ROUTE_PATHS } from "../paths";

export const CalcSettingsListContainer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const permissions = usePermissions();

  const [urlParams] = useSearchParams();
  const calcType = urlParams.get(QueryKeys.CALC_TYPE) as CalcType | null;

  const allowedCalcTypes = useMemo(
    () => [
      ...(hasPermission(permissions, Permission.ADMIN_MTPL_CALCULATORS) ? [CalcType.MTPL] : []),
      ...(hasPermission(permissions, Permission.ADMIN_CRASH_CALCULATORS) ? [CalcType.CRASH] : []),
      ...(hasAllPermissions(permissions, [Permission.ADMIN_MTPL_CALCULATORS, Permission.ADMIN_CRASH_CALCULATORS])
        ? [CalcType.MTPL_CRASH]
        : []),
      ...(hasPermission(permissions, Permission.ADMIN_CRASH_CALCULATORS) ? [CalcType.GAP, CalcType.PAS] : []),
      ...(hasPermission(permissions, Permission.ADMIN_REALTY_CALCULATORS) ? [CalcType.REALTY] : []),
      ...(hasPermission(permissions, Permission.ADMIN_TRAVEL_CALCULATORS) ? [CalcType.TRAVEL] : []),
    ],
    [permissions],
  );

  useEffect(() => {
    if (!calcType || !allowedCalcTypes.includes(calcType)) {
      navigate(
        {
          search: URLQuery.searchParamsToString({
            searchParams: { [QueryKeys.CALC_TYPE]: allowedCalcTypes[0] },
            append: true,
          }),
        },
        { replace: true },
      );
    }
  }, [calcType]);

  const handleCalcTypeSwitch = (calcType: CalcType) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: { [QueryKeys.CALC_TYPE]: calcType },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  const handleCreateClick = (calcType: CalcType) => {
    navigate({
      pathname: ADMIN_CALC_ROUTE_PATHS.create.to,
      search: URLQuery.searchParamsToString({ searchParams: { [QueryKeys.CALC_TYPE]: calcType } }),
    });
  };

  return (
    <ContentWrapper>
      {calcType && (
        <Card
          className="card-box"
          title={<h2>{t("calc.settings.titles.list", { calcType: t("calc.enums.calcType." + calcType) })}</h2>}
          extra={
            <Button
              className="margin-right-small"
              type="primary"
              icon={<AntIcon type="plus" />}
              onClick={() => handleCreateClick(calcType)}
            >
              {t("calc.settings.actions.createSettings", { calcType: t("calc.enums.calcType." + calcType) })}
            </Button>
          }
        >
          <Row gutter={rowGutter} justify="center" className="margin-bottom-medium">
            <Col xs={24} xxl={15} className="center-align">
              <Radio.Group
                size="large"
                buttonStyle="solid"
                value={calcType}
                onChange={(e) => handleCalcTypeSwitch(e.target.value)}
              >
                {allowedCalcTypes.map((type) => (
                  <Radio.Button value={type} key={type}>
                    {t("calc.enums.calcType." + type)}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Col>
          </Row>

          <Row gutter={rowGutter} justify="center">
            <Col xs={24} xxl={14}>
              <CalcSettingsListView />
            </Col>
          </Row>
        </Card>
      )}
    </ContentWrapper>
  );
};
